/**
 * @fileoverview Design Tokens - Sistema de Design Valuation DCF
 * 
 * @description
 * Tokens centralizados extraídos do website institucional Sétima.
 * Base para o custom MUI theme.
 * 
 * @source DESIGN_SYSTEM_VALUATION.md
 * @date 23 de outubro de 2025
 */

// ============================================================================
// MATERIAL DESIGN 3 COLOR SYSTEM
// ============================================================================
// Exportado do Material Theme Builder (https://material-foundation.github.io/material-theme-builder/)
// Seed color: Verde Sétima #006A63 (Teal profissional)
// Scheme: DARK (otimizado para dark mode)
// Garantia: WCAG AAA em todas as combinações

export const colors = {
  // PRIMARY (Verde Sétima - Identidade da marca)
  // Seed: #006A63 - Tom teal sofisticado e profissional
  primary: {
    main: '#81D5CC',           // primary (dark scheme)
    light: '#9DF2E8',          // primaryContainer
    dark: '#00504A',           // onPrimaryContainer (tom escuro)
    container: '#00504A',      // primaryContainer
    onContainer: '#9DF2E8',    // onPrimaryContainer
    contrastText: '#003733',   // onPrimary
  },

  // SECONDARY (Azul-acinzentado - Hierarquia e suporte)
  secondary: {
    main: '#B1CCC8',           // secondary
    light: '#CCE8E4',          // secondaryContainer
    dark: '#324B48',           // onSecondaryContainer
    container: '#324B48',      // secondaryContainer
    onContainer: '#CCE8E4',    // onSecondaryContainer
    contrastText: '#1C3532',   // onSecondary
  },

  // TERTIARY (Azul céu - Contraste e destaque)
  tertiary: {
    main: '#AFC9E7',           // tertiary
    light: '#CEE5FF',          // tertiaryContainer
    dark: '#2F4961',           // onTertiaryContainer
    container: '#2F4961',      // tertiaryContainer
    onContainer: '#CEE5FF',    // onTertiaryContainer
    contrastText: '#17324A',   // onTertiary
  },

  // ERROR (Vermelho semântico - Alertas e erros)
  error: {
    main: '#FFB4AB',           // error
    light: '#FFDAD6',          // errorContainer
    dark: '#93000A',           // onErrorContainer
    container: '#93000A',      // errorContainer
    onContainer: '#FFDAD6',    // onErrorContainer
    contrastText: '#690005',   // onError
  },

  // SUCCESS (Verde positivo - Reutiliza primary)
  success: {
    main: '#81D5CC',           // Reutiliza primary para consistência
    light: '#9DF2E8',
    dark: '#00504A',
    container: '#00504A',
    onContainer: '#9DF2E8',
    contrastText: '#003733',
  },

  // WARNING (Amarelo/Laranja - Avisos)
  warning: {
    main: '#F4BF96',           // Custom Warning-80
    light: '#FFDDB3',          // Custom Warning-90
    dark: '#6A3E1E',           // Custom Warning Container
    container: '#6A3E1E',
    onContainer: '#FFDBAF',
    contrastText: '#4A2800',
  },

  // INFO (Azul informativo - Usa tertiary)
  info: {
    main: '#AFC9E7',           // Reutiliza tertiary
    light: '#CEE5FF',
    dark: '#2F4961',
    container: '#2F4961',
    onContainer: '#CEE5FF',
    contrastText: '#17324A',
  },

  // NEUTRAL (Paleta MD3 oficial - Base do sistema)
  neutral: {
    0: '#000000',
    5: '#0F1111',
    10: '#1A1C1C',
    15: '#242626',
    20: '#2F3130',
    25: '#3A3C3B',
    30: '#454746',
    35: '#515352',
    40: '#5D5F5E',
    50: '#767776',
    60: '#8F9190',
    70: '#AAABAA',
    80: '#C6C7C5',
    90: '#E2E3E1',
    95: '#F0F1EF',
    98: '#F9F9F8',
    99: '#FCFCFB',
    100: '#FFFFFF',
  },

  // NEUTRAL VARIANT (Paleta MD3 oficial - Variações sutis com matiz verde)
  neutralVariant: {
    0: '#000000',
    5: '#0C1211',
    10: '#171D1C',
    15: '#212726',
    20: '#2B3231',
    25: '#363D3C',
    30: '#424847',
    35: '#4D5452',
    40: '#59605E',
    50: '#727877',
    60: '#8C9290',
    70: '#A6ADAB',
    80: '#C2C8C6',
    90: '#DEE4E2',
    95: '#ECF2F0',
    98: '#F5FBF9',
    99: '#F8FEFB',
    100: '#FFFFFF',
  },

  // Background Escuro (Tema Padrão)
  dark: {
    main: '#0E1514',        // surface (MD3) - Background principal
    elevated: '#161D1C',    // surfaceContainerLow - Cards e painéis
    surface: '#1A2120',     // surfaceContainer - Superfícies elevadas
  },

  // Background Claro (Tema Alternativo - não usado, mas mantido para compatibilidade)
  light: {
    main: '#F4FBF9',        // background (light scheme)
    surface: '#FFFFFF',     // Branco puro
  },

  // SURFACES (Sistema de elevação MD3 oficial)
  surface: {
    dim: '#0E1514',              // surfaceDim
    default: '#0E1514',          // surface / background
    bright: '#343A39',           // surfaceBright
    containerLowest: '#090F0F',  // surfaceContainerLowest
    containerLow: '#161D1C',     // surfaceContainerLow
    container: '#1A2120',        // surfaceContainer
    containerHigh: '#252B2A',    // surfaceContainerHigh
    containerHighest: '#303635', // surfaceContainerHighest
  },

  // Textos (MD3 oficial - usando paleta neutral)
  text: {
    primary: '#DDE4E2',     // onSurface (MD3) - WCAG AAA
    secondary: '#BEC9C6',   // onSurfaceVariant (MD3) - WCAG AA+
    tertiary: '#899391',    // outline (MD3) - WCAG AA
    disabled: '#3F4947',    // surfaceVariant (MD3) - Texto desabilitado
    inverse: '#161D1C',     // onBackground inverse (MD3)
  },

  // Cenários (Valuation) - MD3 SEMANTIC COLORS
  // Cores derivadas da paleta MD3 oficial para cenários de valuation
  scenarios: {
    pessimistic: '#FFB4AB',   // error.main (MD3) - Tom coral suave
    base: '#AFC9E7',          // tertiary.main (MD3) - Azul céu profissional
    optimistic: '#81D5CC',    // primary.main (MD3) - Verde teal sofisticado
  },

  // Empresas (Charts) - MD3 COLORS
  // Cores derivadas da paleta MD3 para diferenciação visual
  companies: {
    setima: '#AFC9E7',        // tertiary.main (MD3) - Azul céu
    studios555: '#B1CCC8',    // secondary.main (MD3) - Azul-acinzentado
    consolidated: '#81D5CC',  // primary.main (MD3) - Verde teal (identidade)
  },

  // Estados (Variações) - MD3 SEMANTIC STATES
  // Cores semânticas para indicar mudanças e variações
  states: {
    positive: '#81D5CC',      // primary.main (MD3) - Verde positivo
    negative: '#FFB4AB',      // error.main (MD3) - Coral negativo
    neutral: '#BEC9C6',       // onSurfaceVariant (MD3) - Neutro
  },

  // Bordas
  border: {
    default: 'rgba(255, 255, 255, 0.1)',
    light: 'rgba(19, 23, 26, 0.1)',
  },

  // NOVO: Sistema de Badges Semânticos (WCAG AA compliant)
  badges: {
    info: {
      bg: 'rgba(96, 165, 250, 0.15)',    // Azul suave
      text: '#93C5FD',                    // Azul claro legível
      border: 'rgba(96, 165, 250, 0.3)',
    },
    success: {
      bg: 'rgba(74, 222, 128, 0.15)',    // Verde suave
      text: '#86EFAC',                    // Verde claro legível
      border: 'rgba(74, 222, 128, 0.3)',
    },
    warning: {
      bg: 'rgba(251, 191, 36, 0.15)',    // Amarelo/laranja suave
      text: '#FCD34D',                    // Amarelo claro legível
      border: 'rgba(251, 191, 36, 0.3)',
    },
    error: {
      bg: 'rgba(239, 68, 68, 0.15)',     // Vermelho suave
      text: '#FCA5A5',                    // Vermelho claro legível
      border: 'rgba(239, 68, 68, 0.3)',
    },
    neutral: {
      bg: 'rgba(156, 163, 175, 0.15)',   // Cinza suave
      text: '#D1D5DB',                    // Cinza claro legível
      border: 'rgba(156, 163, 175, 0.3)',
    },
  },

  // Backgrounds (tabelas, superfícies)
  surfaces: {
    zebra: {
      even: '#fafafa',     // Linhas pares (zebra striping)
      odd: '#ffffff',      // Linhas ímpares
      hover: '#f0f0f0',    // Hover state
    },
    border: {
      light: '#f0f0f0',    // Bordas claras
      medium: '#e0e0e0',   // Bordas médias
    },
  },
};

// ============================================================================
// TIPOGRAFIA
// ============================================================================

export const typography = {
  // Família de Fontes
  fontFamily: {
    primary: '"Ubuntu", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"Ubuntu Mono", "Fira Code", "Courier New", monospace',
  },

  // Pesos de Fonte
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    bold: 700,
  },

  // Escala Tipográfica
  fontSize: {
    // Headings
    h1: '72px',
    h2: '48px',
    h3: '36px',
    h4: '24px',
    h5: '20px',
    h6: '18px',

    // Body
    bodyLarge: '23px',
    body: '18px',
    bodySmall: '12px',

    // Números e Dados
    display: '64px',
    metric: '48px',
    data: '32px',
  },

  // Line Heights
  lineHeight: {
    tight: 1.1,
    snug: 1.2,
    normal: 1.5,
    relaxed: 1.6,
  },

  // Letter Spacing
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.05em',
    wider: '0.1em',
  },
};

// ============================================================================
// ESPAÇAMENTO
// ============================================================================

export const spacing = {
  unit: 8, // Base unit

  // Escala de Espaçamento (em pixels)
  xs: 4,     // 0.5 × base
  sm: 8,     // 1 × base
  md: 16,    // 2 × base
  lg: 24,    // 3 × base
  xl: 32,    // 4 × base
  '2xl': 48, // 6 × base
  '3xl': 64, // 8 × base
  '4xl': 96, // 12 × base
};

// ============================================================================
// LAYOUT
// ============================================================================

export const layout = {
  // Container Sizes
  container: {
    maxWidth: '1440px',
    padding: '0 63px',
  },
  containerCompact: {
    maxWidth: '1280px',
    padding: '0 80px',
  },

  // Breakpoints (em pixels)
  breakpoints: {
    xs: 0,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1440,
  },
};

// ============================================================================
// BORDAS E RAIOS
// ============================================================================

export const borders = {
  radius: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '20px',
    '3xl': '24px',
    pill: '30px',  // Para botões
  },

  width: {
    thin: '1px',
    medium: '2px',
    thick: '3px',
  },
};

// ============================================================================
// SOMBRAS
// ============================================================================

export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.15)',
  glow: '0 0 20px rgba(0, 245, 185, 0.3)',
  glowStrong: '0 0 40px rgba(0, 245, 185, 0.6)',
};

// ============================================================================
// TRANSIÇÕES
// ============================================================================

export const transitions = {
  duration: {
    fast: '0.15s',
    base: '0.3s',
    slow: '0.5s',
  },

  easing: {
    base: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// ============================================================================
// ÍCONES
// ============================================================================

export const icons = {
  sizes: {
    xs: 12,
    small: 16,
    medium: 20,
    large: 24,
    xl: 32,
    xxl: 48,
  },
};

// ============================================================================
// GRÁFICOS
// ============================================================================

export const charts = {
  colors: {
    primary: '#00F5B9',
    secondary: '#3B82F6',
    tertiary: '#FFA502',
    quaternary: '#9B59B6',
  },

  grid: {
    color: 'rgba(255, 255, 255, 0.1)',
    axisColor: 'rgba(255, 255, 255, 0.3)',
  },
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

const designTokens = {
  colors,
  typography,
  spacing,
  layout,
  borders,
  shadows,
  transitions,
  icons,
  charts,
};

export default designTokens;
